import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiAppFetch } from '../utils/apiFetch';
import {
  buildWeekBillFileName,
  downloadWeekBillPdf,
  LANDSCAPE_BILL_WIDTH,
} from '../utils/weekBillPdf';
import {
  appendLiveCloseRows,
  buildGrandTotals,
  buildScriptBillData,
  formatAmount,
  formatBillHeaderDate,
  formatExpiryDate,
  groupOrdersByStock,
  mergeTradeClientBrokerageFields,
} from '../utils/weekBillHelpers';
import '../WeekBill.css';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

function WeekBill() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('s') ?? '';
  const endDate = searchParams.get('e') ?? '';
  const userId = Number(localStorage.getItem('userId'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');
  const [scriptBills, setScriptBills] = useState([]);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [zoom, setZoom] = useState(1);
  const [fitScale, setFitScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const viewportRef = useRef(null);
  const billPdfRef = useRef(null);
  const generatingRef = useRef(false);

  const fileName = buildWeekBillFileName(startDate, endDate);
  const displayScale = fitScale * zoom;

  const updateFitScale = useCallback(() => {
    if (!viewportRef.current) return;
    const viewportWidth = viewportRef.current.clientWidth;
    if (viewportWidth > 0) {
      setFitScale(viewportWidth / LANDSCAPE_BILL_WIDTH);
    }
  }, []);

  const updateContentHeight = useCallback(() => {
    if (!billPdfRef.current) return;
    setContentHeight(billPdfRef.current.scrollHeight);
  }, []);

  const handleUnauthorized = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadBill = async () => {
      if (!startDate || !endDate || !userId) {
        setError('Invalid week bill parameters.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const dateRangeBody = JSON.stringify({
          start_date: `${startDate} 00:00:00`,
          end_date: `${endDate} 23:59:59`,
        });

        const [clientRes, pricesRes, ordersRes] = await Promise.all([
          apiAppFetch('getUserDetails', { method: 'POST', body: JSON.stringify({}) }),
          apiAppFetch('getLastPrices', { method: 'POST', body: JSON.stringify({}) }),
          apiAppFetch('ordersByDate', { method: 'POST', body: dateRangeBody }),
        ]);

        if (clientRes.status === 401 || pricesRes.status === 401 || ordersRes.status === 401) {
          handleUnauthorized();
          return;
        }

        if (!clientRes.ok || !pricesRes.ok || !ordersRes.ok) {
          throw new Error('Week bill API request failed.');
        }

        const clientData = mergeTradeClientBrokerageFields(await clientRes.json());
        const lastPrices = await pricesRes.json();
        const orders = await ordersRes.json();

        if (cancelled) return;

        setClientName(clientData?.clientName ?? '');

        const ordersWithLive = appendLiveCloseRows(
          Array.isArray(orders) ? orders : [],
          lastPrices && typeof lastPrices === 'object' ? lastPrices : {}
        );
        const grouped = groupOrdersByStock(ordersWithLive);
        const bills = Object.values(grouped).map((stockOrders) => {
          const first = stockOrders[0];
          return {
            stockName: first.stockName,
            stockExpiry: first.stockExpiry,
            ...buildScriptBillData(clientData, stockOrders),
          };
        });

        setScriptBills(bills);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load week bill.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBill();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, userId, handleUnauthorized]);

  useEffect(() => {
    updateFitScale();
    window.addEventListener('resize', updateFitScale);
    return () => window.removeEventListener('resize', updateFitScale);
  }, [updateFitScale]);

  useEffect(() => {
    if (!viewportRef.current) return undefined;

    const observer = new ResizeObserver(() => {
      updateFitScale();
    });
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [updateFitScale]);

  useEffect(() => {
    if (!billPdfRef.current) return undefined;

    updateContentHeight();
    const observer = new ResizeObserver(() => {
      updateContentHeight();
    });
    observer.observe(billPdfRef.current);
    return () => observer.disconnect();
  }, [loading, scriptBills, clientName, updateContentHeight]);

  const grandTotals = useMemo(() => buildGrandTotals(scriptBills), [scriptBills]);

  const handleZoomIn = () => {
    setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
  };

  const handleZoomOut = () => {
    setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  const handleDownloadPdf = useCallback(async () => {
    if (!billPdfRef.current || generatingRef.current) return;

    generatingRef.current = true;
    setPdfGenerating(true);
    setDownloadMessage('');

    try {
      const saved = await downloadWeekBillPdf(billPdfRef.current, fileName);
      setDownloadMessage(
        saved
          ? 'PDF download started. Check your downloads folder.'
          : 'Could not download PDF. Please try again.'
      );
    } catch (err) {
      console.error('Week bill PDF failed:', err);
      setDownloadMessage('PDF download failed. Please try again.');
    } finally {
      generatingRef.current = false;
      setPdfGenerating(false);
    }
  }, [fileName]);

  const handleClose = () => {
    if (window.ReactNativeWebView) {
      navigate(-1);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    window.close();
  };

  const rangeLabel = `${formatBillHeaderDate(startDate)} to ${formatBillHeaderDate(endDate)}`;

  return (
    <main className="week-bill-page">
      <div className="week-bill-toolbar noprint">
        <div className="week-bill-zoom-controls">
          <button type="button" onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">
            −
          </button>
          <span className="week-bill-zoom-label">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
            +
          </button>
          <button type="button" onClick={handleZoomReset} aria-label="Fit to width">
            Fit
          </button>
        </div>
        <div className="week-bill-toolbar-actions">
          <button
            type="button"
            className="week-bill-download-btn"
            onClick={handleDownloadPdf}
            disabled={pdfGenerating || loading || !!error || scriptBills.length === 0}
          >
            {pdfGenerating ? 'Preparing PDF...' : 'PDF'}
          </button>
          <button type="button" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>

      {downloadMessage && (
        <div className="week-bill-download-message">{downloadMessage}</div>
      )}

      <div ref={viewportRef} className="week-bill-viewport">
        <div
          className="week-bill-scale-wrapper"
          style={{
            width: `${LANDSCAPE_BILL_WIDTH * displayScale}px`,
            height: contentHeight ? `${contentHeight * displayScale}px` : 'auto',
          }}
        >
          <div
            ref={billPdfRef}
            className="week-bill-pdf-root"
            style={{
              width: `${LANDSCAPE_BILL_WIDTH}px`,
              transform: `scale(${displayScale})`,
            }}
          >
            <div className="week-bill-header">
              <table>
                <tbody>
                  <tr>
                    <td style={{ width: '33%', textAlign: 'left' }}>
                      <h1>{clientName || 'Week Bill'}</h1>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <h1>
                        {clientName ? `${clientName} ` : ''}({userId})
                      </h1>
                    </td>
                    <td className="week-bill-range" style={{ width: '33%' }}>
                      <b>{rangeLabel}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {loading && <div className="week-bill-loading">Loading week bill...</div>}
            {!loading && error && <div className="week-bill-error">{error}</div>}
            {!loading && !error && scriptBills.length === 0 && (
              <div className="week-bill-empty">No trades found for this week.</div>
            )}

            {!loading && !error && scriptBills.map((bill) => (
              <section key={`${bill.stockName}-${bill.stockExpiry}`} className="week-bill-script-block">
                <h4 className="week-bill-script-title">
                  {bill.stockName} {formatExpiryDate(bill.stockExpiry)}
                </h4>
                <div className="week-bill-table-wrap">
                  <table className="week-bill-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Execution Time</th>
                        <th>Type</th>
                        <th>Lots</th>
                        <th>Buy QTY</th>
                        <th>Buy Rate</th>
                        <th>Sell QTY</th>
                        <th>Sell Rate</th>
                        <th>Gross Aamount</th>
                        <th>Brokerage</th>
                        <th>Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bill.rows.map((row) => (
                        <tr key={`${row.order.orderId}-${row.index}`}>
                          <td>{row.index}</td>
                          <td>{row.order.exeDateTime}</td>
                          <td className={row.isLive ? 'week-bill-sell' : ''}>
                            {row.cfbf !== 'NA' && !row.isLive ? (
                              <span className="week-bill-sell">{row.cfbf}</span>
                            ) : (
                              row.typeLabel
                            )}
                          </td>
                          <td>{formatAmount(row.order.orderLotQuantity)}</td>
                          {row.isBuy ? (
                            <>
                              <td className="week-bill-buy">{formatAmount(row.order.orderQuantity)}</td>
                              <td className="week-bill-buy">{formatAmount(row.order.orderPrice)}</td>
                              <td />
                              <td />
                            </>
                          ) : (
                            <>
                              <td />
                              <td />
                              <td className="week-bill-sell">{formatAmount(row.order.orderQuantity)}</td>
                              <td className="week-bill-sell">{formatAmount(row.order.orderPrice)}</td>
                            </>
                          )}
                          <td className={row.isBuy ? 'week-bill-buy' : 'week-bill-sell'}>
                            {row.isBuy ? formatAmount(row.grossLine) : `-${formatAmount(row.grossLine)}`}
                          </td>
                          <td>-{formatAmount(row.orderBrokerage)}</td>
                          <td className={row.isBuy ? 'week-bill-buy' : 'week-bill-sell'}>
                            {row.isBuy
                              ? formatAmount(row.netAmount)
                              : `-${formatAmount(row.netAmount)}`}
                          </td>
                        </tr>
                      ))}
                      <tr className="week-bill-total-row">
                        <td />
                        <td>Total</td>
                        <td colSpan={2} />
                        <td>{bill.totals.totalBuyTrades ? formatAmount(bill.totals.totalBuyTrades) : ''}</td>
                        <td>
                          {bill.totals.avgBuyPrice ? formatAmount(bill.totals.avgBuyPrice) : ''}
                        </td>
                        <td>{bill.totals.totalSellTrades ? formatAmount(bill.totals.totalSellTrades) : ''}</td>
                        <td>
                          {bill.totals.avgSellPrice ? formatAmount(bill.totals.avgSellPrice) : ''}
                        </td>
                        <td className={bill.totals.totalGrossAmount < 0 ? 'week-bill-sell' : 'week-bill-buy'}>
                          {formatAmount(bill.totals.totalGrossAmount)}
                        </td>
                        <td>-{formatAmount(bill.totals.totalBrokerage)}</td>
                        <td className={bill.totals.totalNetAmount < 0 ? 'week-bill-sell' : 'week-bill-buy'}>
                          {formatAmount(bill.totals.totalNetAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>&nbsp;</div>
              </section>
            ))}

            {!loading && !error && scriptBills.length > 0 && (
              <table className="week-bill-grand">
                <tbody>
                  <tr>
                    <td className={grandTotals.grandGrossAmount < 0 ? 'week-bill-sell' : 'week-bill-buy'}>
                      Total Gross: {formatAmount(grandTotals.grandGrossAmount)}
                    </td>
                    <td>
                      Total Brokerage: -{formatAmount(grandTotals.grandBrokerage)}
                    </td>
                    <td className={grandTotals.grandNetAmount < 0 ? 'week-bill-sell' : 'week-bill-buy'}>
                      Final P&L: {formatAmount(grandTotals.grandNetAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default WeekBill;
